import { useState } from 'react'
import { useToast } from '@/hooks/use-toast'
import { useAuthenticatedApi } from './useAuthenticatedApi'

interface RouletteResult {
  result: string
  xp_change: number
  new_xp_balance: number
  can_spin_again: boolean
  next_spin_at: string | null
}

interface RouletteStatus {
  can_spin: boolean
  has_spun_today: boolean
  today_result: {
    result: string
    xp_change: number
    spun_at: string
  } | null
  current_xp: number
  next_spin_at: string | null
}

interface RouletteHistory {
  history: Array<{
    id: string
    result: string
    xp_change: number
    created_at: string
  }>
  stats: {
    total_spins: number
    total_xp_gained: number
    win_count: number
    loss_count: number
    win_rate: number
  }
}

export const useRouletteSystem = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()
  const { invokeFunction } = useAuthenticatedApi()

  const spinRoulette = async (): Promise<RouletteResult | null> => {
    setLoading(true)
    setError(null)

    console.log(`🎰 [ROULETTE] Attempting to spin roulette`);
    console.log(`🎰 [ROULETTE] Auth state check:`, {
      connected_wallet_address: localStorage.getItem('connected_wallet_address'),
      bromentum_wallet_address: localStorage.getItem('bromentum_wallet_address'),
      walletAddress: localStorage.getItem('walletAddress')
    });

    try {
      const data = await invokeFunction('roulette-manager', {
        action: 'spin'
      })

      console.log(`🎰 [ROULETTE] Function response:`, data);

      if (!data.success) {
        throw new Error(data.error)
      }

      const result = data as RouletteResult

      // Show appropriate toast based on result
      const isWin = result.xp_change > 0
      toast({
        title: isWin ? "🎉 You Won!" : "😔 You Lost",
        description: `${isWin ? '+' : ''}${result.xp_change} BroScore. New balance: ${result.new_xp_balance}`,
        variant: isWin ? "default" : "destructive",
      })

      console.log(`🎰 [ROULETTE] Spin successful:`, result);
      return result
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to spin roulette'
      setError(errorMessage)
      
      console.error(`🎰 [ROULETTE] Spin failed:`, err);
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
      return null
    } finally {
      setLoading(false)
    }
  }

  const getDailyStatus = async (): Promise<RouletteStatus | null> => {
    setLoading(true)
    setError(null)

    try {
      const data = await invokeFunction('roulette-manager', {
        action: 'get_daily_status'
      })

      if (!data.success) {
        throw new Error(data.error)
      }

      return data as RouletteStatus
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to get daily status'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }

  const getHistory = async (): Promise<RouletteHistory | null> => {
    setLoading(true)
    setError(null)

    try {
      const data = await invokeFunction('roulette-manager', {
        action: 'get_history'
      })

      if (!data.success) {
        throw new Error(data.error)
      }

      return data as RouletteHistory
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to get history'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }

  const clearError = () => setError(null)

  return {
    loading,
    error,
    spinRoulette,
    getDailyStatus,
    getHistory,
    clearError
  }
}